import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { OptStatus } from "@modules/cashplus/entities/cashplus.model";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { CashplusNavigationService } from "@modules/cashplus/infrastructure/cashplus-navigation.service";
import { map } from "rxjs/operators";
import { AppropriatenessFacade } from "@modules/main/components/stocks/appropriateness-modal/+state/appropriateness.facade";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";

@Injectable()
export class CashplusOptInFacade {
	private isApiInLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private readonly cashPlusFacade: CashPlusFacade,
		private readonly cashPlusNavigationService: CashplusNavigationService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly appropriatenessFacade: AppropriatenessFacade,
		private readonly launchDarkly: LaunchDarklyService
	) {}

	public onOptInRequest(): void {
		this.isApiInLoading$.next(true);
		this.cashPlusFacade
			.updateOptStatus$(OptStatus.OptIn)
			.subscribe({
				next: () => {
					this.cashPlusFacade.updateSubscriptionState(true);
					this.infoService.setSuccess(this.translationService.translate("cash_plus_opt_in_message"));
					this.cashPlusNavigationService.closeModal();
				},
				error: () => {
					this.infoService.setError(this.translationService.translate("cash_plus_opt_error_message"));
				}
			})
			.add(() => {
				this.isApiInLoading$.next(false);
			});
	}

	public optInViewModel$(): Observable<OptInViewModelInterface> {
		return combineLatest([
			this.isApiInLoading$,
			this.cashPlusFacade.getFiatsSymbolCashPlus$(),
			this.appropriatenessFacade.isStockNotAppropriateOrNotExperienced$(),
			this.launchDarkly.isActive$(LD_FLAG.LIMIT_ORDERS_BUY),
			this.launchDarkly.isActive$(LD_FLAG.LIMIT_ORDERS_SELL)
		]).pipe(
			map(([loading, symbols, userNotAppropriate, isBuyLimitActive, isSellLimitActive]) => ({
				isLoading: loading,
				isCashPlusSingleSymbol: symbols.length === 1,
				singularSymbol: symbols.slice(0, 1).toString(),
				firstsSymbol: symbols
					.slice(0, -1)
					.map((symbol) => (this.isGerman() ? symbol + "-" : symbol))
					.join(", "),
				lastSymbol: symbols
					.slice(-1)
					.map((symbol) => (this.isGerman() ? symbol + "-" : symbol))
					.toString(),
				isUserNotAppropriate: userNotAppropriate,
				isLimitOrdersEnabled: isBuyLimitActive || isSellLimitActive
			}))
		);
	}

	public isGerman(): boolean {
		return this.translationService.getLanguage() === "deu";
	}
}

export interface OptInViewModelInterface {
	isLoading: boolean;
	isCashPlusSingleSymbol: boolean;
	singularSymbol: string;
	firstsSymbol: string;
	lastSymbol: string;
	isUserNotAppropriate: boolean;
	isLimitOrdersEnabled: boolean;
}
